import {
    formatCodeableConcept,
    getDisplayString,
    getReferenceString
} from '@medplum/core'
import {
    DiagnosticReport,
    Observation,
    ObservationReferenceRange,
    Organization,
    Practitioner,
    Reference,
    Resource,
    ServiceRequest
} from '@medplum/fhirtypes'
import { LabResultResource } from 'src/pages/MUIBased/LabResults/hooks/useGetLabResults'

type RowData = {
    id: string
    orderDate?: Date
    requester: string
    test: string
    labPerformer: string
    results?: Result[]
}

export type Result = {
    id: string
    testCode: string
    testName: string
    valueString?: string
    valueQuantity?: number
    unit?: string
    referenceRange?: ObservationReferenceRange
    minRange?: number
    maxRange?: number
    interpretation: string
}

export const searchResultsToRows = (
    rootResourceArray: LabResultResource[]
): RowData[] => {
    function mapById<T>(resources: Resource[], resourceType: string) {
        return (
            resources
                ?.filter(e => e?.resourceType === resourceType)
                .reduce((map, resource) => {
                    if (resource?.id) {
                        map.set(getReferenceString(resource), resource as T)
                    }
                    return map
                }, new Map<string, T>()) ?? new Map<string, T>()
        )
    }

    const obsMap: Map<string, Observation> = mapById(
        rootResourceArray ?? [],
        'Observation'
    )

    const srMap: Map<string, ServiceRequest> = mapById(
        rootResourceArray ?? [],
        'ServiceRequest'
    )

    const pracMap: Map<string, Practitioner> = mapById(
        rootResourceArray ?? [],
        'Practitioner'
    )

    const orgMap: Map<string, Organization> = mapById(
        rootResourceArray ?? [],
        'Organization'
    )

    const rootDiagReports = (rootResourceArray.filter(
        e => e?.resourceType === 'DiagnosticReport'
    ) ?? []) as DiagnosticReport[]

    const results =
        rootDiagReports.flatMap(rootDiagReport => {
            const obsRefs = rootDiagReport?.result

            const serviceRequest = srMap.get(
                rootDiagReport?.basedOn?.[0].reference ?? ''
            )

            const practitioner = pracMap.get(
                serviceRequest?.requester?.reference ?? ''
            )

            const observations = obsRefs
                ?.map(obsRef => obsMap.get(obsRef.reference!))
                .filter(obs => !!obs)

            const refOrg = serviceRequest?.performer?.[0]?.reference

            const performingLabOrg = orgMap.get(refOrg!)

            return (
                observations?.map(obs => ({
                    id: obs?.id ?? '',
                    orderDate: obs?.issued ? new Date(obs?.issued) : undefined,
                    requester: practitioner
                        ? getDisplayString(practitioner)
                        : '',
                    test: formatCodeableConcept(obs?.code),
                    labPerformer: performingLabOrg
                        ? getDisplayString(performingLabOrg)
                        : '',
                    results:
                        // We found some cases where there were duplicate references in hasMember.
                        // To prevent this we added removeDuplicates to prevent duplicate references
                        removeDuplicates(obs?.hasMember)?.map(member => {
                            if (!member.reference) {
                                throw new Error('Member reference is missing')
                            }
                            const memberObs = obsMap.get(member.reference)
                            return {
                                id: memberObs?.id ?? '',
                                testCode:
                                    memberObs?.code?.coding?.[0]?.code ?? '',
                                testName: formatCodeableConcept(
                                    memberObs?.code
                                ),
                                valueString: memberObs?.valueString,
                                valueQuantity: memberObs?.valueQuantity?.value,
                                referenceRange: memberObs?.referenceRange?.[0],
                                unit: memberObs?.valueQuantity?.unit,
                                interpretation:
                                    memberObs?.interpretation?.[0]?.coding?.[0]
                                        .display ?? ''
                            }
                        }) ?? []
                })) ?? []
            )
        }) ?? []

    const sortedResults = results.sort((a, b) =>
        a.orderDate && b.orderDate
            ? b.orderDate.getTime() - a.orderDate.getTime()
            : 0
    )

    return sortedResults
}

export function removeDuplicates(references?: Reference<Resource>[]) {
    if (!references) {
        return
    }
    const uniqueRefs = new Map<string, Reference<Resource>>()
    references.forEach(ref => {
        if (ref.reference) {
            uniqueRefs.set(ref.reference, ref)
        }
    })
    return Array.from(uniqueRefs.values())
}
